import { type FC, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import ChevronDown from 'dibs-icons/exports/ChevronDown.ts';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import {
    PdpSharedSimilarSoldItemsHeader,
    PdpSharedSimilarSoldItemsSubHeader,
} from '../PdpSharedSimilarSoldItems/PdpSharedSimilarSoldItemsHeader.tsx';
import { PdpSharedSimilarSoldItemsColumns } from '../PdpSharedSimilarSoldItems/PdpSharedSimilarSoldItemsColumns.tsx';
import { usePdpSharedSimilarSoldItemsContext } from '../PdpSharedSimilarSoldItems/PdpSharedSimilarSoldItemsContext/PdpSharedSimilarSoldItemsContext.tsx';
import { useShouldShowSimilarSoldItems } from '../hooks/useShouldShowSimilarSoldItems.ts';
import { useSimilarSoldItemsTracking } from '../hooks/useSimilarSoldItemsTracking.ts';
import styles from './PdpRespSimilarSoldItems.module.css';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpRespSimilarSoldItems_viewer$key } from './__generated__/PdpRespSimilarSoldItems_viewer.graphql.ts';
import { type PdpRespSimilarSoldItems_item$key } from './__generated__/PdpRespSimilarSoldItems_item.graphql.ts';
import { type PdpRespSimilarSoldItems_user$key } from './__generated__/PdpRespSimilarSoldItems_user.graphql.ts';

const viewerFragment = graphql`
    fragment PdpRespSimilarSoldItems_viewer on Viewer {
        ...useShouldShowSimilarSoldItems_viewer
        ...PdpSharedSimilarSoldItemsColumns_viewer
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarSoldItems_item on Item {
        ...PdpSharedSimilarSoldItemsHeader_item
        ...PdpSharedSimilarSoldItemsColumns_item
        ...PdpSharedContactSellerButton_item
        ...useShouldShowSimilarSoldItems_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSimilarSoldItems_user on User {
        ...PdpSharedSimilarSoldItemsColumn_user
    }
`;

export const PdpRespSimilarSoldItemsGradient: FC = () => (
    <div
        className={classnames(
            styles.gradient,
            dibsCss.h200px,
            dibsCss.wFull,
            dibsCss.absolute,
            dibsCss.bottom0
        )}
    />
);

const PdpRespSimilarSoldItems: FC<{
    viewer: PdpRespSimilarSoldItems_viewer$key;
    item: PdpRespSimilarSoldItems_item$key;
    user?: PdpRespSimilarSoldItems_user$key | null;
    currency: string;
}> = ({ viewer: viewerRef, item: itemRef, user: userRef, currency }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { similarSoldItemsSectionRef, isSimilarSoldItemsOpen, setIsSimilarSoldItemsOpen } =
        usePdpSharedSimilarSoldItemsContext();
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});
    const { isBelowMinPriceEstimate } = useShouldShowSimilarSoldItems({
        viewer,
        item,
        currency,
        afterDisplay,
    });

    const {
        trackSimilarSoldItemsModuleClick,
        trackSimilarSoldItemsModuleVisible,
        trackSimilarSoldItemsEcommerceImpressions,
    } = useSimilarSoldItemsTracking({ viewer });

    useEffect(() => {
        if (viewer && isSimilarSoldItemsOpen) {
            trackSimilarSoldItemsEcommerceImpressions();
        }
    }, [viewer, isSimilarSoldItemsOpen, trackSimilarSoldItemsEcommerceImpressions]);

    return (
        <GridRow>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackSimilarSoldItemsModuleVisible();
                    }
                }}
            />
            <GridCol desktop={12} tabletPortrait={12} tabletLandscape={12}>
                <div
                    className={classnames(
                        dibsCss.sassyFontBodyMediumHeavy,
                        dibsCss.textNoir,
                        dibsCss.mtMedium
                    )}
                >
                    <PdpSharedSimilarSoldItemsHeader item={item} />
                </div>
                <div
                    ref={similarSoldItemsSectionRef}
                    className={classnames(
                        dibsCss.relative,
                        dibsCss.overflowHidden,
                        styles.expandableArea,
                        {
                            [dibsCss.maxH600px]: isSimilarSoldItemsOpen,
                            [dibsCss.maxH300px]: !isSimilarSoldItemsOpen,
                            [dibsCss.cursorPointer]: !isSimilarSoldItemsOpen,
                        }
                    )}
                    onClick={() => {
                        if (!isSimilarSoldItemsOpen) {
                            trackSimilarSoldItemsModuleClick();
                        }
                        setIsSimilarSoldItemsOpen(true);
                    }}
                    onKeyDown={() => {
                        if (!isSimilarSoldItemsOpen) {
                            trackSimilarSoldItemsModuleClick();
                        }
                        setIsSimilarSoldItemsOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <div
                        className={classnames(
                            dibsCss.sassyFontBodySmall,
                            dibsCss.textNoir,
                            dibsCss.mtXxsmall
                        )}
                    >
                        <PdpSharedSimilarSoldItemsSubHeader
                            isBelowMinPriceEstimate={isBelowMinPriceEstimate}
                        />
                    </div>
                    <PdpSharedSimilarSoldItemsColumns
                        viewer={viewer}
                        item={item}
                        user={user}
                        currency={currency}
                        className={dibsCss.mtXsmall}
                    />
                    {!isSimilarSoldItemsOpen && <PdpRespSimilarSoldItemsGradient />}
                </div>
                {!isSimilarSoldItemsOpen && (
                    <Link
                        underline="none"
                        onClick={() => {
                            trackSimilarSoldItemsModuleClick();
                            setIsSimilarSoldItemsOpen(true);
                        }}
                        className={classnames(
                            dibsCss.inlineFlex,
                            dibsCss.sassyFontBodySmallHeavy,
                            dibsCss.textNoir
                        )}
                    >
                        <FormattedMessage
                            id="abf.PdpRespSimilarSoldItems.expand"
                            defaultMessage="Expand"
                        />
                        <ChevronDown variation="20" />
                    </Link>
                )}
            </GridCol>
        </GridRow>
    );
};

export default PdpRespSimilarSoldItems;
